import { Concern, requiredCfm, GENERAL_TARGET } from '@/lib/calculator';

/** Room sizes for /200-sq-ft style pages (ft²) */
export const roomSizes = [100, 150, 200, 250, 300, 400, 500, 750, 1000] as const;

export interface RoomTypeConfig {
  slug: string;
  name: string;
  sqft: number;
  ceilingFt: number;
  typicalDimensions: { length: number; width: number };
  concerns: Concern[];
  /** Practical notes for this room. No product claims. */
  notes: string[];
}

export const roomTypes: Record<string, RoomTypeConfig> = {
  bedroom: { slug: 'bedroom', name: 'Bedroom', sqft: 150, ceilingFt: 8, typicalDimensions: { length: 12, width: 12.5 }, concerns: ['general'],
    notes: ['Noise matters most here. Output at a speed you can sleep with is what counts, and few models publish it — a "22 dB" minimum is usually the lowest speed, not the speed that meets your target.', 'Check whether display lights can be turned off.'] },
  'living-room': { slug: 'living-room', name: 'Living room', sqft: 300, ceilingFt: 8, typicalDimensions: { length: 15, width: 20 }, concerns: ['general'],
    notes: ['If the room opens into others, the air you need to clean is larger than the room itself.', 'Two smaller units can be quieter than one large unit at high speed, if they share the same open space.'] },
  'home-office': { slug: 'home-office', name: 'Home office', sqft: 120, ceilingFt: 8, typicalDimensions: { length: 12, width: 10 }, concerns: ['general'],
    notes: ['A small room needs little output; noise at a working speed is usually the limiting factor.'] },
  nursery: { slug: 'nursery', name: 'Nursery', sqft: 100, ceilingFt: 8, typicalDimensions: { length: 10, width: 10 }, concerns: ['general'],
    notes: ['A HEPA purifier removes airborne particles. It does not remove gases released by new furniture or paint — ventilation and low-emission products matter more for that.', 'Keep cords and the unit out of reach.'] },
  studio: { slug: 'studio', name: 'Studio apartment', sqft: 450, ceilingFt: 9, typicalDimensions: { length: 25, width: 18 }, concerns: ['general'],
    notes: ['Size for the whole open space. Kitchen fumes and odours are a gas problem; a range hood vented outside helps more than a purifier.'] },
  basement: { slug: 'basement', name: 'Basement', sqft: 600, ceilingFt: 7, typicalDimensions: { length: 30, width: 20 }, concerns: ['general'],
    notes: ['A musty smell usually means moisture. Fix the damp and control humidity first; a purifier does not fix the source of mould.'] },
  'master-bedroom': { slug: 'master-bedroom', name: 'Main bedroom', sqft: 250, ceilingFt: 8, typicalDimensions: { length: 16, width: 15.6 }, concerns: ['general'],
    notes: ['Larger bedrooms often need a mid-speed setting that is louder than you would like. Compare output at a tolerable noise level where it is published.'] },
  kitchen: { slug: 'kitchen', name: 'Kitchen', sqft: 200, ceilingFt: 8, typicalDimensions: { length: 14, width: 14.3 }, concerns: ['general', 'gases'],
    notes: ['Cooking produces particles and gases (including nitrogen dioxide from gas stoves). A range hood vented outdoors is the main control; a purifier helps only with the particle part.'] },
};

/** Required CADR for a room at this site's default target. */
export function getCadrForSqft(sqft: number, ceilingFt = 8): number {
  return Math.ceil(requiredCfm({ areaSqFt: sqft, ceilingFt }, GENERAL_TARGET.each) - 1e-9);
}

export function parseRoomSizeSlug(slug: string): number | null {
  const m = slug.match(/^(\d+)-sq-ft$/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return (roomSizes as readonly number[]).includes(n) ? n : null;
}

export function getRoomTypeBySlug(slug: string): RoomTypeConfig | null {
  return roomTypes[slug] ?? null;
}
